/**
 * i18next initialization for the IGNODE Label Factory annotator.
 *
 * Per IR-3 plan A.5b (English + Spanish shipped in v1) and IR-8 T12
 * (German + French added to match the canonical en,es,de,fr registry):
 *  - 7 namespaces: common, navigation, canvas, modals, errors, about, license
 *  - Locale detection priority: URL ?lang=es > user preference (IGNODE
 *    mode) > browser Accept-Language > fallback en
 *  - All user-visible strings via t('key'); CI gate flags bare strings
 */
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

// Eagerly bundle the locale resources — they're small JSON. Vite chunks
// the i18n bundle out per vite.config.ts manualChunks so it doesn't bloat
// the initial JS payload.
import enCommon from '../locales/en/common.json';
import enNavigation from '../locales/en/navigation.json';
import enCanvas from '../locales/en/canvas.json';
import enModals from '../locales/en/modals.json';
import enErrors from '../locales/en/errors.json';
import enAbout from '../locales/en/about.json';
import enLicense from '../locales/en/license.json';

import esCommon from '../locales/es/common.json';
import esNavigation from '../locales/es/navigation.json';
import esCanvas from '../locales/es/canvas.json';
import esModals from '../locales/es/modals.json';
import esErrors from '../locales/es/errors.json';
import esAbout from '../locales/es/about.json';
import esLicense from '../locales/es/license.json';

import deCommon from '../locales/de/common.json';
import deNavigation from '../locales/de/navigation.json';
import deCanvas from '../locales/de/canvas.json';
import deModals from '../locales/de/modals.json';
import deErrors from '../locales/de/errors.json';
import deAbout from '../locales/de/about.json';
import deLicense from '../locales/de/license.json';

import frCommon from '../locales/fr/common.json';
import frNavigation from '../locales/fr/navigation.json';
import frCanvas from '../locales/fr/canvas.json';
import frModals from '../locales/fr/modals.json';
import frErrors from '../locales/fr/errors.json';
import frAbout from '../locales/fr/about.json';
import frLicense from '../locales/fr/license.json';

const SUPPORTED = (import.meta.env.VITE_SUPPORTED_LOCALES ?? 'en,es,de,fr')
    .split(',')
    .map((s: string) => s.trim());

const DEFAULT_LOCALE = import.meta.env.VITE_DEFAULT_LOCALE ?? 'en';

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            en: {
                common: enCommon,
                navigation: enNavigation,
                canvas: enCanvas,
                modals: enModals,
                errors: enErrors,
                about: enAbout,
                license: enLicense,
            },
            es: {
                common: esCommon,
                navigation: esNavigation,
                canvas: esCanvas,
                modals: esModals,
                errors: esErrors,
                about: esAbout,
                license: esLicense,
            },
            de: {
                common: deCommon,
                navigation: deNavigation,
                canvas: deCanvas,
                modals: deModals,
                errors: deErrors,
                about: deAbout,
                license: deLicense,
            },
            fr: {
                common: frCommon,
                navigation: frNavigation,
                canvas: frCanvas,
                modals: frModals,
                errors: frErrors,
                about: frAbout,
                license: frLicense,
            },
        },
        fallbackLng: DEFAULT_LOCALE,
        supportedLngs: SUPPORTED,
        ns: ['common', 'navigation', 'canvas', 'modals', 'errors', 'about', 'license'],
        defaultNS: 'common',
        detection: {
            order: ['querystring', 'localStorage', 'navigator'],
            lookupQuerystring: 'lang',
            lookupLocalStorage: 'ignode-annotator-locale',
            caches: ['localStorage'],
        },
        interpolation: { escapeValue: false }, // React already escapes
        returnNull: false,
    });

export default i18n;
